import React from "react";
import { motion } from "framer-motion";

import congralation from '../../assets/inter/congralations.webp'
import {
  GraduationCap,
  BookOpenCheck,
  LineChart,
  Users,
  CheckCircle2,
  Brain,
  MessagesSquare,
  ClipboardList,
  School2,
  Building2,
  Bus,
  ShieldCheck,
  Star,
  Sparkles,
  PhoneCall,
  MapPin,
  Timer,
} from "lucide-react";
import { ContactCTA } from "./Rankridgebipcneet";
import { interslider } from "../data";

/**
 * Rankridge Intermediate – IIT-JEE & NEET Integrated Page
 * Tech: React + Tailwind + Framer Motion + lucide-react icons
 * Notes:
 * - Drop this component anywhere in your React app (e.g., src/pages/RankridgeIntermediate.jsx)
 * - Ensure Tailwind is configured. No additional CSS needed.
 * - Replace placeholder images/logos/links with your assets.
 */

const features = [
  {
    icon: <GraduationCap className="h-6 w-6" />,
    title: "India’s Top Faculty – Mentors Who Care",
    bullets: [
      "Experienced mentors from IITs, NITs & AIIMS",
      "Proven strategies to crack the toughest questions",
      "1-on-1 mentoring so no student is left behind",
      "Teachers who guide for life, not just lectures",
    ],
  },
  {
    icon: <LineChart className="h-6 w-6" />,
    title: "A Legacy of Results",
    bullets: [
      "AIR < 100 in JEE Advanced & 680+ scorers in NEET",
      "Alumni at IITs, AIIMS, NITs & global institutes",
      "Consistent top ranks year after year",
    ],
  },
  {
    icon: <ClipboardList className="h-6 w-6" />,
    title: "Daily · Weekly · Monthly Exams",
    bullets: [
      "Daily Practice Papers (DPPs)",
      "Weekly mock tests simulating real exam conditions",
      "Monthly full-syllabus assessments",
      "Parent performance reports for transparency",
    ],
  },
  {
    icon: <BookOpenCheck className="h-6 w-6" />,
    title: "Finest Study Material (Boards + JEE/NEET)",
    bullets: [
      "Topic-wise flow: Basic → Advanced",
      "Latest exam trends & toppers’ strategies",
      "Dual success: Boards + Competitive",
    ],
  },
  {
    icon: <MessagesSquare className="h-6 w-6" />,
    title: "Doubt Clarification",
    bullets: [
      "Dedicated sessions after each class",
      "1-on-1 mentoring for tough topics",
      "Crash clarifications before exams",
    ],
  },
  {
    icon: <Brain className="h-6 w-6" />,
    title: "Smart & Easy Learning",
    bullets: [
      "Smartboards & visual diagrams",
      "Mind maps & shortcut tricks",
      "Real-life examples for better recall",
    ],
  },
];

const programs = [
  {
    tag: "Residential",
    icon: <Building2 className="h-5 w-5" />,
    points: [
      "Safe hostels with 24×7 academic & personal care",
      "Separate facilities for boys & girls",
    ],
  },
  {
    tag: "Semi‑Residential",
    icon: <School2 className="h-5 w-5" />,
    points: [
      "Full‑day classes + evening study under mentorship",
      "Balanced schedule for focused prep",
    ],
  },
  {
    tag: "Day Scholar",
    icon: <Bus className="h-5 w-5" />,
    points: [
      "Daily classes with safe transport facilities",
      "Best for students staying with family",
    ],
  },
];

const parentsLove = [
  "Stress‑free preparation without sacrificing results",
  "Continuous parent communication & performance updates",
  "Safe, disciplined, motivating environment",
  "Focus on life skills & confidence‑building",
  " Expert Faculty With Proven Track Record",
  " Small Batches With Individual Mentoring",
  "Integrated Coaching for IIT-JEE, NEET & Boards",
  "Regular Assessments & Transparent Reports",
  "Strong Academic Discipline With Emotional Care",
  "Modern Learning With Labs & Technology"

];

const badges = [
  { icon: <ShieldCheck className="h-4 w-4" />, text: "Hyderabad’s Trusted Intermediate College" },
  { icon: <Star className="h-4 w-4" />, text: "Integrated IIT‑JEE & NEET Coaching (MPC/BiPC)" },
  { icon: <Sparkles className="h-4 w-4" />, text: "Proven Results · Mentors · Care" },
];

export default function RankridgeIntermediate() {
  return (
    <div className=" bg-neutral-50 text-neutral-900">
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 -z-10 bg-gradient-to-br from-emerald-100 via-teal-100 to-sky-100" />
        <div className="absolute -top-24 -left-24 h-72 w-72 rounded-full bg-gradient-to-tr from-teal-300/30 to-emerald-300/30 blur-3xl" />
        <div className="absolute -bottom-24 -right-24 h-72 w-72 rounded-full bg-gradient-to-tr from-sky-300/30 to-teal-300/30 blur-3xl" />

        <div className="mx-auto max-w-7xl px-1 py-2 lg:flex lg:items-center lg:gap-1 lg:py-2">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mx-auto max-w-2xl text-center lg:mx-0 lg:text-left"
          >
            <span className="inline-flex items-center gap-2 rounded-full border border-teal-300 bg-white/70 px-3 py-1 text-xs font-semibold text-teal-800 backdrop-blur">
              <Sparkles className="h-3.5 w-3.5" /> Hyderabad’s Best Intermediate College
            </span>
            <h1 className="mt-5 text-4xl font-extrabold leading-tight tracking-tight sm:text-5xl">
              Intermediate with Integrated <span className="text-teal-700">IIT‑JEE</span> & {""}
              <span className="text-emerald-700">NEET</span> Coaching
            </h1>
            <p className="mt-5 text-lg leading-relaxed text-neutral-700">
              Where Bright Futures Begin. At Rankridge Junior College, we blend MPC & BiPC with
              India’s best coaching programs—balancing academics, discipline, and care.
              We don’t just prepare for exams—we prepare for life, confidence, and long‑term success.
            </p>

            <div className="mt-6 flex flex-wrap items-center gap-3">
              {badges.map((b, i) => (
                <span
                  key={i}
                  className="inline-flex items-center gap-2 rounded-full bg-white px-3 py-1 text-xs font-medium text-neutral-700 shadow-sm ring-1 ring-black/5"
                >
                  {b.icon}
                  {b.text}
                </span>
              ))}
            </div>

            <div className="mt-8 flex flex-col items-center gap-3 sm:flex-row lg:items-start">
              <a
                href="tel:+919959271353"
                className="inline-flex items-center justify-center rounded-2xl px-5 py-3 text-sm font-semibold shadow-sm ring-1 ring-black/10 bg-teal-600 text-white hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-teal-600/40"
              >
                <PhoneCall className="mr-2 h-4 w-4" /> Call: +91 99 59 271 353
              </a>
              <a
                href="https://www.rankridge.com/contact-us/"
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center justify-center rounded-2xl px-5 py-3 text-sm font-semibold bg-white text-teal-700 ring-1 ring-black/10 hover:bg-teal-50"
              >
                <MapPin className="mr-2 h-4 w-4" /> Enquiry For Admissions
              </a>
            </div>
          </motion.div>

          {/* Hero art / placeholder */}
          <motion.div
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="mt-12 lg:mt-0 lg:flex-1"
          >
            <div className="relative mx-auto w-full max-w-xl overflow-hidden rounded-3xl bg-white shadow-xl ring-1 ring-black/5">
              <div className="aspect-[16/11] bg-gradient-to-br from-white to-teal-50" />
              <div className="absolute inset-0 grid place-items-center">
                <div className="rounded-2xl border border-dashed border-teal-300/60 bg-white/70  backdrop-blur">
                  {/* <p className="text-center text-sm text-neutral-600">
                    Replace with campus/ classroom hero image 
                    Add a high‑quality campus/classroom image here
                  </p> */}
                  <img src={congralation} className="w-full rounded-2xl object-contain h-auto"   loading="lagy" />
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
{/* About */}
      <section className="mx-auto max-w-7xl px-6 py-1 lg:py-2">
        <div className="grid gap-10 lg:grid-cols-2">
<div className="rounded-3xl bg-white p-6 shadow-sm ring-1 ring-black/5">
            <img src={interslider[1]} className="object-contain rounded-2xl" loading="lazy" />
            
            
            {/* <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
              <div className="rounded-2xl bg-neutral-50 p-4 ring-1 ring-black/5">
                <p className="font-semibold">Streams</p>
                <p className="mt-1 text-neutral-700">MPC & BiPC with Integrated Coaching</p>
              </div>
              <div className="rounded-2xl bg-neutral-50 p-4 ring-1 ring-black/5">
                <p className="font-semibold">Coaching</p>
                <p className="mt-1 text-neutral-700">IIT‑JEE (Mains+Advanced), NEET</p>
              </div>
              <div className="rounded-2xl bg-neutral-50 p-4 ring-1 ring-black/5">
                <p className="font-semibold">Programs</p>
                <p className="mt-1 text-neutral-700">Residential · Semi‑Residential · Day Scholar</p>
              </div>
              <div className="rounded-2xl bg-neutral-50 p-4 ring-1 ring-black/5">
                <p className="font-semibold">Location</p>
                <p className="mt-1 text-neutral-700">Hyderabad, Telangana</p>
              </div>
            </div> */}
          </div>

          <div>
            <h2 className="text-3xl font-bold text-center tracking-tight sm:text-4xl">About Rankridge Intermediate College</h2>
            <p className="mt-4 leading-relaxed text-neutral-700">
              Rankridge Junior College is Hyderabad’s most trusted Intermediate college with integrated IIT‑JEE & NEET coaching,
              offering MPC & BiPC. For two decades, we’ve guided aspiring doctors and engineers with expert faculty,
              advanced study material, and stress‑free learning methods. Our mentors include alumni from IITs, NITs,
              and AIIMS, with a proven track record of top ranks and admissions to premier institutes.
            </p>
            <p className="mt-4 leading-relaxed text-neutral-700">
              We believe in holistic development—small batch sizes, personalized mentoring, doubt clearing, yoga,
              and motivational workshops—so students achieve academic goals while maintaining emotional balance.
              Parents stay closely connected via regular reports, digital tracking, and mentor feedback.
            </p>
            <div className="mt-6 flex flex-wrap items-center gap-3 text-sm text-neutral-700">
              <span className="inline-flex items-center gap-2 rounded-full bg-teal-50 px-3 py-1 ring-1 ring-teal-200 text-teal-700">
                <Users className="h-4 w-4" /> Small Batches
              </span>
              <span className="inline-flex items-center gap-2 rounded-full bg-emerald-50 px-3 py-1 ring-1 ring-emerald-200 text-emerald-700">
                <MessagesSquare className="h-4 w-4" /> Mentor Feedback
              </span>
              <span className="inline-flex items-center gap-2 rounded-full bg-sky-50 px-3 py-1 ring-1 ring-sky-200 text-sky-700">
                <Timer className="h-4 w-4" /> Consistent Assessments
              </span>
            </div>
          </div>

          
        </div>
      </section>

      {/* Why Parents Trust Rankridge */}
      <section className="mx-auto max-w-7xl px-6 py-1 lg:py-2 bg-[#20b2aa]">
        <div className="mx-auto max-w-3xl text-center">
          <h2 className="text-3xl font-bold tracking-tight sm:text-4xl mt-4">Why Parents Across Telangana Trust Rankridge</h2>
          <p className="mt-3 text-black">
            Designed for dual success—Boards + IIT‑JEE/NEET—backed by mentors, measurable progress, and a caring system.
          </p>
        </div>

        <div className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {features.map((f, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.4 }}
              transition={{ duration: 0.4, delay: i * 0.05 }}
              className="group h-full rounded-3xl bg-white p-6 shadow-sm ring-1 ring-black/5 hover:shadow-md"
            >
              <div className="flex items-center gap-3">
                <div className="grid h-10 w-10 place-items-center rounded-2xl bg-teal-50 text-teal-700 ring-1 ring-teal-200">
                  {f.icon}
                </div>
                <h3 className="text-lg font-semibold leading-tight">{f.title}</h3>
              </div>
              <ul className="mt-4 space-y-2 text-sm text-neutral-700">
                {f.bullets.map((b, j) => (
                  <li key={j} className="flex items-start gap-2">
                    <CheckCircle2 className="mt-0.5 h-4 w-4 flex-none text-emerald-600" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>
      </section>
        <div className="absolute inset-x-0 -top-8 mx-auto h-16 w-11/12 rounded-3xl bg-white shadow-xl ring-1 ring-black/5 mt-8" ></div>

      {/* Programs */}
      <section className="relative bg-gradient-to-b from-white to-teal-50/60 py-1 ">
        <div className="relative mx-auto max-w-7xl px-6">
          <div className="mx-auto max-w-3xl text-center">
            <h2 className="text-3xl font-bold tracking-tight sm:text-4xl mt-4">Flexible Learning Modes</h2>
            <p className="mt-3 text-neutral-600">Choose a program that fits your child’s needs.</p>
          </div>

          <div className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {programs.map((p, i) => (
              <div key={i} className="rounded-3xl bg-white p-6 shadow-sm ring-1 ring-black/5">
                <span className="inline-flex items-center gap-2 rounded-full bg-teal-50 px-3 py-1 text-xs font-semibold text-teal-700 ring-1 ring-teal-200">
                  {p.icon}
                  {p.tag}
                </span>
                <ul className="mt-4 space-y-2 text-sm text-neutral-700">
                  {p.points.map((pt, j) => (
                    <li key={j} className="flex items-start gap-2">
                      <CheckCircle2 className="mt-0.5 h-4 w-4 flex-none text-emerald-600" />
                      <span>{pt}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Parents love */}
      <section className="mx-auto max-w-7xl px-6 py-1">
        <div className="grid items-center gap-10 lg:grid-cols-2">
          <div>
            <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">What Parents Love About Rankridge</h2>
            <ul className="mt-6 space-y-3 text-neutral-700">
              {parentsLove.map((p, i) => (
                <li key={i} className="flex items-start gap-3 text-base">
                  <CheckCircle2 className="mt-1 h-5 w-5 flex-none text-emerald-600" />
                  <span>{p}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Visual card */}
          <div className="rounded-3xl bg-white p-6 shadow-sm ring-1 ring-black/5">
            <img src={interslider[3]} className="object-contain rounded-2xl" loading="lazy" />
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative isolate mx-4 overflow-hidden rounded-3xl bg-gradient-to-r from-teal-600 via-emerald-600 to-teal-700 px-6 py-12 text-white shadow-xl sm:mx-auto sm:max-w-7xl sm:px-12">
        <div className="mx-auto max-w-3xl text-center">
          <h3 className="text-2xl font-bold tracking-tight sm:text-3xl">Admissions Open – Limited Seats for 2025</h3>
          <p className="mt-2 text-white/90">
            Join Hyderabad’s premier Intermediate college for integrated IIT‑JEE & NEET coaching. Give your child the Rankridge edge.
          </p>
          <div className="mt-6 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <a
              href="tel:+919959271353"
              className="inline-flex items-center justify-center rounded-2xl bg-white px-5 py-3 text-sm font-semibold text-teal-700 ring-1 ring-white/20 hover:bg-white/95"
            >
              <PhoneCall className="mr-2 h-4 w-4" /> Call: +91 99 59 271 353
            </a>
            <a
              href="https://www.rankridge.com"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center justify-center rounded-2xl bg-white/10 px-5 py-3 text-sm font-semibold text-white ring-1 ring-white/30 hover:bg-white/15"
            >
              <MapPin className="mr-2 h-4 w-4" /> Visit Website
            </a>
          </div>
        </div>
        <div className="pointer-events-none absolute -right-24 -top-24 h-64 w-64 rounded-full bg-white/10 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-24 -left-24 h-64 w-64 rounded-full bg-white/10 blur-3xl" />
      </section>
<section>
  <ContactCTA />
</section>
      

      {/* Footer */}
      <footer className="border-t bg-white">
        <div className="mx-auto max-w-7xl px-6 py-10">
          <div className="flex flex-col items-center justify-between gap-4 sm:flex-row">
            <p className="text-center text-sm text-neutral-600">
              © {new Date().getFullYear()} Rankridge Junior College · All rights reserved
            </p>
            <div className="flex flex-wrap items-center justify-center gap-2 text-xs text-neutral-500">
              <span>Privacy</span>
              <span>·</span>
              <span>Terms</span>
              <span>·</span>
              <span>Admissions</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
